import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { MdWorkspacesOutline } from "react-icons/md";
import { FaDatabase, FaBook } from "react-icons/fa";
import { LuVariable } from "react-icons/lu";
import { GiAlarmClock } from "react-icons/gi";
import { AiOutlineSetting } from "react-icons/ai";
import { CiClock1 } from "react-icons/ci";
import { RiDashboard3Line } from "react-icons/ri";
import Title from "../common/Title";
import DesktopWindow from "../product/DesktopWindow";
import ConnectorGrid from "../connector/ConnectorGrid";
import ConnectorForm from "../connector/ConnectorForm";
import VariableGrid from "../variable/VariableGrid";
import VariableForm from "../variable/VariableForm";
import SchedulerGrid from "../scheduler/SchedulerGrid";
import SchedulerForm from "../scheduler/SchedulerForm";
import RunbookGrid from "../runbook/RunbookGrid";
import RunbookForm from "../runbook/RunbookForm";
import RunbookExecutionHistoryGrid from "../runbook/RunbookExecutionHistoryGrid";
import RunbookExecutionHistoryLineGrid from "../runbook/RunbookExecutionHistoryLineGrid";
import WorkflowBuilder from "../workflow/WorkflowBuilder";
import { BsDiagram3 } from "react-icons/bs";
import SettingsHub from "../settings/SettingsHub";
import LatestExecutions from "../dashboard/LatestExecutions";
import NextExecutions from "../dashboard/NextExecutions";
import RamBotBubble from "./RamBotBubble";
import Utils from "../../utils";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useDesktopWindows, WINDOW_START_Y, WINDOW_CASCADE_OFFSET } from "../../hooks/useDesktopWindows";
import { useDesktopIconPositions } from "../../hooks/useDesktopIconPositions";
import armaninoLogo from "../../assets/armanino-llp.png";
import armaninoLogoDark from "../../assets/Armanino-logo-reversed.png";
import "./WorkspaceHub.css";

// Pilot for the "whole site as a desktop" pattern already shipped for Administration
// (see SettingsHub/ProductHub) — each entry is a top-level nav item turned into a desktop
// icon. `to` is the virtual /workspace/* path this window syncs to; `standaloneTo` is the
// real routed page mobile falls back to (windows are desktop-only, same as the other hubs).
// `visible` gates icons that aren't available to every authenticated user (e.g.
// Administration mirrors LeftMenu's own admin/superUser/productTeam check); omit it to
// show the icon to everyone. Administration's window embeds the real SettingsHub
// component — its icons/windows are `position: fixed` (viewport-relative, not clipped by
// whatever contains them), so they float freely over the whole screen same as any other
// window here; only its own duplicate "Administration" title needed suppressing.
// `maximize` opens near-full-viewport but leaves a visible margin on every side (still
// reads as a floating window); `fill` (Runbooks only) goes fully edge-to-edge, covering
// the sidebar too — see useDesktopWindows for both.
// `requiresClient` mirrors LeftMenu's existing disabled-without-a-client treatment for
// these same four areas — their data fetches are scoped to `user.clientId`, so there's
// nothing useful to show without one selected.
const ICONS = [
  // Latest/Next Executions from the Dashboard page, bundled into one window.
  { id: "dashboard", label: "Dashboard", to: "/workspace/dashboard", standaloneTo: "/dashboard", opensWindow: true, Icon: RiDashboard3Line, color: "#cf7f00", maximize: true, requiresClient: true },
  { id: "runbooks", label: "My Runbooks", to: "/workspace/runbooks", standaloneTo: "/runbook", opensWindow: true, Icon: FaBook, color: "#1d4ed8", maximize: true, requiresClient: true },
  { id: "connectors", label: "My Connectors", to: "/workspace/connectors", standaloneTo: "/connector", opensWindow: true, Icon: FaDatabase, color: "#0f766e", maximize: true, requiresClient: true },
  { id: "variables", label: "My Variables", to: "/workspace/variables", standaloneTo: "/variable", opensWindow: true, Icon: LuVariable, color: "#7c5295", maximize: true, requiresClient: true },
  { id: "schedulers", label: "My Schedulers", to: "/workspace/schedulers", standaloneTo: "/scheduler", opensWindow: true, Icon: GiAlarmClock, color: "#b36d00", maximize: true, requiresClient: true },
  {
    id: "administration",
    label: "Administration",
    to: "/workspace/administration",
    standaloneTo: "/settings",
    opensWindow: true,
    Icon: AiOutlineSetting,
    color: "#334155",
    // Administration is itself a whole desktop (SettingsHub, embedded) — `maximize` (what
    // Runbooks/Connectors/Variables/Schedulers use) still leaves a visible margin on every
    // side, which reads as too cramped for a nested desktop; `fill` goes fully edge-to-edge
    // instead (see fillLayout in useDesktopWindows.js).
    fill: true,
    visible: (user) => !!(user?.isSuperUser || user?.isAdmin || user?.isProductTeam),
  },
];

function getIcon(id) {
  return ICONS.find((i) => i.id === id);
}

const WINDOW_Z_BASE = 500;
// The sticky app header sits above windows in z-index, so a window dragged past this
// point has its title bar hidden underneath it and becomes unreachable.
const WINDOW_MIN_Y = 65;

const STORAGE_KEY = "workspaceHubIconPositions";

// IDs for Runbooks' extra drill-down windows (list -> executions -> lines, and the
// Designer), which don't fit the generic "one form window per area" shape the other areas
// use. The Designer is a true singleton (one fixed ID — editing a second runbook's
// workflow replaces what's showing). Executions and Lines are NOT: unlike every other
// window in this hub, a user may reasonably want two runbooks' histories open side by
// side, so each gets an id keyed to what it's showing — one window per runbook for
// Executions, one per execution for Lines. Re-opening the SAME runbook/execution still
// reuses its window (openOrUpdateWindow's normal behavior); only a DIFFERENT one opens a
// second window.
const RUNBOOK_EXECUTIONS_WINDOW_PREFIX = "runbook-executions-";
const RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX = "runbook-execution-lines-";
const runbookExecutionsWindowId = (runbookId) => `${RUNBOOK_EXECUTIONS_WINDOW_PREFIX}${runbookId}`;
const runbookExecutionLinesWindowId = (executionId) => `${RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX}${executionId}`;
const RUNBOOK_DESIGNER_WINDOW_ID = "runbook-designer";

// Grids are dense enough that the default 640x480 cascade is too cramped; roughly centered
// and generously sized without fully covering whatever window opened it. `offset` staggers
// stacked drill-down windows (e.g. Lines on top of Executions) so a newly-opened one is
// visibly offset from its parent instead of landing at the exact same position/size, which
// otherwise makes it look like nothing happened when it opens directly on top.
function largeSizing(offset = 0) {
  const width = Math.min(1000, window.innerWidth - 32);
  const height = Math.min(700, window.innerHeight - 96);
  const baseX = Math.max(40, (window.innerWidth - width) / 2);
  return {
    x: Math.min(baseX + offset, Math.max(16, window.innerWidth - width - 16)),
    y: WINDOW_START_Y + offset,
    width,
    height,
  };
}

// Execution History (and its Lines drill-down) always open as a real separate browser
// window instead of floating in-page — stacking several of these as DesktopWindow overlays
// reads as clutter/nesting (see the "Lines opens inside Executions inside the desktop"
// screenshot that prompted this). Pointed at the `/popup` routes (App.jsx) — siblings of the
// normal `/runbook/executions...` routes but rendered WITHOUT Layout's header/sidebar, so
// the popup reads like a payment provider's popup: just the report, none of the app's own
// navigation chrome, and not a second copy of the whole workspace desktop shell either.
// `name` is a real window.open target name, not a DOM id: passing the SAME name twice makes
// the browser refocus/navigate that existing popup instead of opening a new one, which is
// what gives "one popup per runbook" (or per execution) for free, without any of this hub's
// own window-state bookkeeping — a different name always opens a new popup.
function popOutRunbookWindow(name, to, sizing) {
  const layout = typeof sizing === "function" ? sizing([]) : sizing;
  window.open(
    to,
    name,
    `width=${layout.width},height=${layout.height},left=${layout.x},top=${layout.y}`
  );
}

// Per-area wiring for the "list window" <-> "add/edit form window" pairing: `idParam` is
// the query-string key used both here and on the area's legacy standalone route,
// `formTitle` labels the form window, and render* wire hideTitle plus the window-aware
// callback props each grid/form already supports (see the Connectors/Variables refactor).
// `extra` carries handlers that don't fit the generic (onAdd, onEdit) shape — currently
// just Runbooks' executions drill-down and its Scheduler cross-link.
function buildAreaConfig(user, extra) {
  return {
    runbooks: {
      formWindowId: "runbook-form",
      idParam: "runbookId",
      formTitle: () => "Add Runbook",
      renderGrid: (onAdd) => (
        <RunbookGrid
          user={user}
          hideTitle
          onAddRunbook={onAdd}
          onOpenExecutions={extra.onOpenExecutions}
          onAddScheduler={extra.onAddScheduler}
          onOpenDesigner={extra.onOpenDesigner}
        />
      ),
      renderForm: (itemId, onDone) => (
        <RunbookForm user={user} hideTitle onSaveSuccess={onDone} onCancel={onDone} />
      ),
    },
    connectors: {
      formWindowId: "connector-form",
      idParam: "connectorId",
      formTitle: (mode) => (mode === "edit" ? "Edit Connector" : "Add Connector"),
      // The Connector form is dense (OnPrem agent card, secret fields, etc.) — give it
      // more room than the default 640x480 cascade size.
      formSizing: () => {
        const width = Math.min(900, window.innerWidth - 32);
        const height = Math.min(780, window.innerHeight - 96);
        return { x: Math.max(40, (window.innerWidth - width) / 2), y: WINDOW_START_Y, width, height };
      },
      renderGrid: (onAdd, onEdit) => (
        <ConnectorGrid user={user} hideTitle onAddConnector={onAdd} onEditConnector={onEdit} />
      ),
      renderForm: (itemId, onDone) => (
        <ConnectorForm user={user} hideTitle presetConnectorId={itemId} onSaveSuccess={onDone} onCancel={onDone} />
      ),
    },
    variables: {
      formWindowId: "variable-form",
      idParam: "variableId",
      formTitle: (mode) => (mode === "edit" ? "Edit Variable" : "Add Variable"),
      renderGrid: (onAdd, onEdit) => (
        <VariableGrid user={user} hideTitle onAddVariable={onAdd} onEditVariable={onEdit} />
      ),
      renderForm: (itemId, onDone) => (
        <VariableForm user={user} hideTitle presetVariableId={itemId} onSaveSuccess={onDone} onCancel={onDone} />
      ),
    },
    // Schedulers has no in-window "Add" of its own: a scheduler is only ever created
    // against a specific runbook, either from My Runbooks (extra.onAddScheduler) or not at
    // all — matching what the standalone /scheduler page supports today.
    schedulers: {
      formWindowId: "scheduler-form",
      idParam: "schedulerId",
      formTitle: (mode) => (mode === "add" ? "Add Scheduler" : "Edit Scheduler"),
      renderGrid: (onAdd, onEdit) => (
        <SchedulerGrid user={user} hideTitle onEditScheduler={onEdit} />
      ),
      renderForm: (itemId, onDone) => (
        <SchedulerForm user={user} hideTitle presetSchedulerId={itemId} onSaveSuccess={onDone} onCancel={onDone} />
      ),
    },
  };
}

// `pinned` (Administration): a system/admin area, not a personal workspace tool like the
// other icons, so it doesn't live in their draggable grid — fixed spot via CSS (see
// `.workspace-desktop-icon.pinned` in WorkspaceHub.css) and a "pinned" class for the
// visually-distinct dock-like treatment. Drag itself is turned off via useDraggable's own
// `disabled` option, NOT by omitting `attributes`/`listeners` — those still carry the
// tabIndex/role/keyboard wiring dnd-kit needs for a properly interactive element; dropping
// them entirely left the div with no accessible-interactive semantics at all, which is what
// made it render like inert page content (native browser selection highlight) instead of a
// button-like icon.
function DesktopIcon({ icon, position, isSelected, isDisabled, onSelect, onOpen, pinned = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: icon.id, disabled: pinned });
  const { Icon } = icon;

  const style = pinned
    ? undefined
    : {
        transform: `translate3d(${position.x + (transform?.x ?? 0)}px, ${position.y + (transform?.y ?? 0)}px, 0)`,
        zIndex: isDragging ? 10 : 1,
      };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      className={`workspace-desktop-icon${pinned ? " pinned" : ""}${isSelected ? " selected" : ""}${isDragging ? " dragging" : ""}${isDisabled ? " disabled" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(icon.id);
      }}
      onDoubleClick={() => !isDisabled && onOpen(icon)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isDisabled) onOpen(icon);
      }}
      aria-label={icon.label}
      aria-disabled={isDisabled || undefined}
      title={isDisabled ? "Select a client first" : undefined}
    >
      <div className="workspace-desktop-icon-glyph" style={{ "--tile-color": icon.color }}>
        <Icon />
      </div>
      <span className="workspace-desktop-icon-label">{icon.label}</span>
    </div>
  );
}

export default function WorkspaceHub({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useDarkMode();
  const containerRef = useRef(null);
  const watermarkRef = useRef(null);
  // Lets handleCloseWindow ask the Designer whether it has unsaved canvas edits before
  // actually closing it — the window's own close button bypasses WorkflowBuilder's internal
  // Apply & Close/Discard flows entirely, so without this, closing it would silently drop
  // any unsaved work.
  const workflowBuilderRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const {
    openWindows,
    openWindow,
    openOrUpdateWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    toggleMaximizeWindow,
    handleWindowResize,
    handleWindowDragEnd,
  } = useDesktopWindows({ minY: WINDOW_MIN_Y });
  // CoryBot (KbBuilderBubble, inside WorkflowBuilder) and GraceBot (GraceBotBubble, inside
  // RunbookExecutionHistoryLineGrid) are each their own floating bot mounted at RamBot's same
  // default corner -- open (not necessarily focused/maximized) is enough for them to be "there".
  const hasCoryOrGraceWindowOpen = openWindows.some(
    (win) => win.id === RUNBOOK_DESIGNER_WINDOW_ID || win.id.startsWith(RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX)
  );
  const visibleIcons = ICONS.filter((icon) => !icon.visible || icon.visible(user));
  // Administration is pinned separately (see DesktopIcon's `pinned` handling below) — kept
  // out of the draggable grid entirely, so it doesn't participate in the grid/centering math.
  const gridIcons = visibleIcons.filter((icon) => icon.id !== "administration");
  const pinnedIcon = visibleIcons.find((icon) => icon.id === "administration");

  const { positions, handleDragEnd, handleReset } = useDesktopIconPositions({
    icons: gridIcons,
    storageKey: STORAGE_KEY,
    containerRef,
    avoidRef: watermarkRef,
    cols: 5,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );
  const windowSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const openFormWindow = (areaId, { mode, itemId }) => {
    const area = areas[areaId];
    const icon = getIcon(areaId);
    openOrUpdateWindow(area.formWindowId, {
      title: area.formTitle(mode),
      Icon: icon.Icon,
      color: icon.color,
      meta: { mode, itemId },
      sizing: area.formSizing,
    });
  };

  // Lets My Runbooks' "Add Scheduler" open the Schedulers area's existing form window
  // instead of leaving the workspace — SchedulerForm already knows how to seed itself from
  // a runbookId in the query string (its pre-existing "create from a runbook" flow), so
  // putting it in the URL here is all that's needed; no prop plumbing required.
  const openSchedulerAddForRunbook = (runbookId) => {
    openFormWindow("schedulers", { mode: "add" });
    navigate(`/workspace/schedulers/add?runbookId=${runbookId}`);
  };

  // Pure state setters for the Runbooks drill-down (no navigate) — used both by click
  // handlers (paired with a navigate below) and by the URL-sync effect on refresh/deep-link,
  // which must NOT re-navigate or it would clobber the very URL it's reacting to.
  const openRunbookExecutionsWindow = ({ runbookId, runbookGuid, runbookName }) => {
    const qs = new URLSearchParams({ runbookId, ...(runbookGuid ? { runbookGuid } : {}) }).toString();
    openOrUpdateWindow(runbookExecutionsWindowId(runbookId), {
      title: runbookName ? `Execution History — ${runbookName}` : "Execution History",
      Icon: CiClock1,
      color: getIcon("runbooks").color,
      meta: { runbookId, runbookGuid },
      to: `/workspace/runbooks/executions?${qs}`,
      // Cascades against however many OTHER runbooks' Executions windows are already
      // open, so a second one doesn't land exactly on top of the first.
      sizing: (prev) =>
        largeSizing(prev.filter((w) => w.id.startsWith(RUNBOOK_EXECUTIONS_WINDOW_PREFIX)).length * WINDOW_CASCADE_OFFSET),
    });
  };

  const openRunbookExecutionLinesWindow = ({ executionId, runbookId }) => {
    openOrUpdateWindow(runbookExecutionLinesWindowId(executionId), {
      title: "Execution History Lines",
      Icon: CiClock1,
      color: getIcon("runbooks").color,
      meta: { executionId, runbookId },
      to: `/workspace/runbooks/executions/lines?executionId=${executionId}&runbookId=${runbookId}`,
      // Base 48px stagger from the Executions window it drilled down from, plus further
      // cascade against however many OTHER executions' Lines windows are already open.
      sizing: (prev) =>
        largeSizing(48 + prev.filter((w) => w.id.startsWith(RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX)).length * WINDOW_CASCADE_OFFSET),
    });
  };

  const handleOpenExecutions = ({ runbookId, runbookGuid }) => {
    const qs = new URLSearchParams({ runbookId, ...(runbookGuid ? { runbookGuid } : {}) }).toString();
    popOutRunbookWindow(`popout-${runbookExecutionsWindowId(runbookId)}`, `/runbook/executions/popup?${qs}`, largeSizing());
  };

  const handleOpenExecutionLines = (executionId, runbookId) => {
    popOutRunbookWindow(
      `popout-${runbookExecutionLinesWindowId(executionId)}`,
      `/runbook/executions/lines/popup?executionId=${executionId}&runbookId=${runbookId}`,
      largeSizing(48)
    );
  };

  // `win` is the specific Executions window being closed (one runbook's) — also closes any
  // Lines windows that were drilled into from THIS runbook's history specifically, leaving
  // other runbooks' Executions/Lines windows untouched.
  const closeRunbookExecutionsWindow = (win) => {
    closeWindow(win.id);
    const runbookId = win.meta?.runbookId;
    openWindows
      .filter((w) => w.id.startsWith(RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX) && w.meta?.runbookId === runbookId)
      .forEach((w) => closeWindow(w.id));
    navigate("/workspace/runbooks");
  };

  // `win` is the specific Lines window being closed (one execution's).
  const closeRunbookExecutionLinesWindow = (win) => {
    closeWindow(win.id);
    const runbookId = win.meta?.runbookId;
    const execWin = runbookId ? openWindows.find((w) => w.id === runbookExecutionsWindowId(runbookId)) : null;
    if (execWin?.meta?.runbookId) {
      const qs = new URLSearchParams({
        runbookId: execWin.meta.runbookId,
        ...(execWin.meta.runbookGuid ? { runbookGuid: execWin.meta.runbookGuid } : {}),
      }).toString();
      navigate(`/workspace/runbooks/executions?${qs}`);
    } else {
      navigate("/workspace/runbooks");
    }
  };

  // No URL sync/pop-out for the Designer: it's driven entirely by sessionStorage
  // (importWorkflow/editingVersion, set by RunbookGrid before calling this), which doesn't
  // reliably survive a fresh page load or a separate popped-out tab — there's nothing
  // meaningful to deep-link back into, unlike the other drill-downs.
  const openRunbookDesignerWindow = ({ runbookName } = {}) => {
    openOrUpdateWindow(RUNBOOK_DESIGNER_WINDOW_ID, {
      title: runbookName ? `Designer — ${runbookName}` : "Designer",
      Icon: BsDiagram3,
      color: getIcon("runbooks").color,
      // The canvas wants as much room as it can get — its internal sizing assumes a
      // near-full-viewport host.
      sizing: () => {
        const margin = 40;
        return {
          x: margin,
          y: WINDOW_START_Y,
          width: window.innerWidth - margin * 2,
          height: window.innerHeight - WINDOW_START_Y - margin,
        };
      },
    });
  };

  const areas = buildAreaConfig(user, {
    onOpenExecutions: handleOpenExecutions,
    onAddScheduler: openSchedulerAddForRunbook,
    onOpenDesigner: openRunbookDesignerWindow,
  });

  // Keeps the open windows in sync with the URL: landing on /workspace/<area> (fresh
  // load, refresh, or a shared link) reopens that area's window, /<area>/add or
  // /<area>/edit?<idParam>=... additionally reopens the add/edit form on top of it, and
  // Runbooks' executions/lines drill-down is synced the same way via its own paths.
  useEffect(() => {
    const relPath = location.pathname.replace(/^\/workspace\/?/, "");
    ICONS.forEach((icon) => {
      if (!icon.opensWindow || !relPath.startsWith(icon.id)) return;
      openWindow(icon);
      const area = areas[icon.id];
      if (!area) return;
      if (relPath === `${icon.id}/add`) {
        openFormWindow(icon.id, { mode: "add" });
      } else if (relPath === `${icon.id}/edit`) {
        const itemId = Utils.getQueryStringParams(location)[area.idParam];
        openFormWindow(icon.id, { mode: "edit", itemId });
      }
    });

    if (relPath === "runbooks/executions" || relPath === "runbooks/executions/lines") {
      const { runbookId, runbookGuid } = Utils.getQueryStringParams(location);
      if (runbookId) openRunbookExecutionsWindow({ runbookId, runbookGuid });
    }
    if (relPath === "runbooks/executions/lines") {
      const { executionId, runbookId } = Utils.getQueryStringParams(location);
      if (executionId) openRunbookExecutionLinesWindow({ executionId, runbookId });
    }
    // eslint-disable-next-line
  }, [location.pathname, location.search]);

  const handleAdd = (areaId) => {
    openFormWindow(areaId, { mode: "add" });
    navigate(`/workspace/${areaId}/add`);
  };

  const handleEdit = (areaId, itemId) => {
    openFormWindow(areaId, { mode: "edit", itemId });
    navigate(`/workspace/${areaId}/edit?${areas[areaId].idParam}=${itemId}`);
  };

  const closeFormWindow = (areaId) => {
    closeWindow(areas[areaId].formWindowId);
    navigate(`/workspace/${areaId}`);
  };

  // Pops the window's route open in its own browser window (reusing the same session
  // cookies) and closes the floating copy, since the two would otherwise show duplicate
  // live state for the same feature.
  const handlePopOutWindow = (win) => {
    if (!win.to) return;
    window.open(win.to, `popout-${win.id}`, `width=${win.width},height=${win.height},left=${win.x},top=${win.y}`);
    handleCloseWindow(win.id);
  };

  const handleCloseWindow = (id) => {
    if (id === RUNBOOK_DESIGNER_WINDOW_ID && workflowBuilderRef.current?.hasUnsavedChanges()) {
      const confirmed = window.confirm(
        "You have unsaved changes in the Designer. Close anyway and discard them?"
      );
      if (!confirmed) return;
    }
    if (id.startsWith(RUNBOOK_EXECUTIONS_WINDOW_PREFIX)) {
      closeRunbookExecutionsWindow(openWindows.find((w) => w.id === id));
      return;
    }
    if (id.startsWith(RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX)) {
      closeRunbookExecutionLinesWindow(openWindows.find((w) => w.id === id));
      return;
    }
    closeWindow(id);
    const icon = getIcon(id);
    if (icon) {
      if (location.pathname.startsWith(`/workspace/${id}`)) navigate("/workspace");
      return;
    }
    const areaId = Object.keys(areas).find((key) => areas[key].formWindowId === id);
    if (areaId && location.pathname.startsWith(`/workspace/${areaId}/`)) {
      navigate(`/workspace/${areaId}`);
    }
  };

  const renderWindowBody = (win) => {
    if (win.id === "dashboard") {
      return (
        <div className="d-flex gap-3 align-items-start">
          <div className="flex-fill" style={{ minWidth: 0 }}>
            <LatestExecutions user={user} />
          </div>
          <div className="flex-fill" style={{ minWidth: 0 }}>
            <NextExecutions user={user} />
          </div>
        </div>
      );
    }
    if (win.id.startsWith(RUNBOOK_EXECUTIONS_WINDOW_PREFIX)) {
      return (
        <RunbookExecutionHistoryGrid
          user={user}
          hideTitle
          runbookId={win.meta?.runbookId}
          runbookGuid={win.meta?.runbookGuid}
          onOpenLines={(executionId, runbookId) => handleOpenExecutionLines(executionId, runbookId ?? win.meta?.runbookId)}
        />
      );
    }
    if (win.id.startsWith(RUNBOOK_EXECUTION_LINES_WINDOW_PREFIX)) {
      return (
        <RunbookExecutionHistoryLineGrid
          user={user}
          hideTitle
          runbookId={win.meta?.runbookId}
          executionId={win.meta?.executionId}
        />
      );
    }
    if (win.id === RUNBOOK_DESIGNER_WINDOW_ID) {
      return (
        <WorkflowBuilder
          ref={workflowBuilderRef}
          user={user}
          hideTitle
          onDone={() => handleCloseWindow(RUNBOOK_DESIGNER_WINDOW_ID)}
        />
      );
    }
    const icon = getIcon(win.id);
    if (icon) {
      const area = areas[icon.id];
      if (!area) {
        return <SettingsHub user={user} hideTitle />;
      }
      return area.renderGrid(() => handleAdd(icon.id), (itemId) => handleEdit(icon.id, itemId));
    }
    const areaId = Object.keys(areas).find((key) => areas[key].formWindowId === win.id);
    if (areaId) {
      return areas[areaId].renderForm(win.meta?.itemId, () => closeFormWindow(areaId));
    }
    return null;
  };

  const isIconDisabled = (icon) => !!(icon.requiresClient && !user?.clientId);

  const handleOpen = (icon) => {
    if (isIconDisabled(icon)) return;
    if (icon.opensWindow) {
      openWindow(icon);
    }
    navigate(icon.to);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Title text="Workspace" icon={<MdWorkspacesOutline className="mb-1" />} />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-none d-md-inline-block"
          onClick={handleReset}
        >
          Reset Layout
        </button>
      </div>

      <div className="workspace-desktop d-none d-md-block" ref={containerRef} onClick={() => setSelectedId(null)}>
        <img
          ref={watermarkRef}
          className="workspace-desktop-watermark"
          src={darkMode ? armaninoLogoDark : armaninoLogo}
          alt=""
          aria-hidden="true"
        />
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          {gridIcons.map((icon) => (
            <DesktopIcon
              key={icon.id}
              icon={icon}
              position={positions[icon.id]}
              isSelected={selectedId === icon.id}
              isDisabled={isIconDisabled(icon)}
              onSelect={setSelectedId}
              onOpen={handleOpen}
            />
          ))}
        </DndContext>

        {pinnedIcon && (
          <DesktopIcon
            icon={pinnedIcon}
            pinned
            isSelected={selectedId === pinnedIcon.id}
            isDisabled={isIconDisabled(pinnedIcon)}
            onSelect={setSelectedId}
            onOpen={handleOpen}
          />
        )}
      </div>

      <DndContext sensors={windowSensors} onDragEnd={handleWindowDragEnd}>
        {openWindows.map((win) => (
          <DesktopWindow
            key={win.id}
            id={win.id}
            title={win.title}
            icon={<win.Icon />}
            accentColor={win.color}
            position={{ x: win.x, y: win.y }}
            size={{ width: win.width, height: win.height }}
            zIndex={WINDOW_Z_BASE + win.zIndex}
            minY={WINDOW_MIN_Y}
            isMinimized={win.isMinimized}
            isMaximized={win.isMaximized}
            onClose={handleCloseWindow}
            onFocus={() => focusWindow(win.id)}
            onResize={handleWindowResize}
            onMinimize={minimizeWindow}
            onMaximizeToggle={toggleMaximizeWindow}
            onPopOut={win.to ? () => handlePopOutWindow(win) : undefined}
          >
            {renderWindowBody(win)}
          </DesktopWindow>
        ))}
      </DndContext>

      <div className="workspace-desktop-mobile d-md-none">
        {visibleIcons.map((icon) => {
          const disabled = isIconDisabled(icon);
          return (
            <button
              key={icon.id}
              type="button"
              className={`workspace-desktop-mobile-item${disabled ? " disabled" : ""}`}
              disabled={disabled}
              title={disabled ? "Select a client first" : undefined}
              onClick={() => navigate(icon.standaloneTo)}
            >
              <span className="workspace-desktop-icon-glyph" style={{ "--tile-color": icon.color }}>
                <icon.Icon />
              </span>
              <span className="workspace-desktop-icon-label">{icon.label}</span>
            </button>
          );
        })}
      </div>

      {/* Don't show RamBot alongside CoryBot (runbook designer) or GraceBot (execution lines) --
          both are their own floating bots mounted inside those windows, at the same default
          bottom-right corner RamBot uses, so showing all of them at once would overlap. */}
      {(user?.isSuperUser || user?.isProductTeam) && !hasCoryOrGraceWindowOpen && <RamBotBubble />}
    </>
  );
}
